import { LoadStatus } from '../../load-status';
import { Action, createReducer, on } from '@ngrx/store';
import {
    cleanupCompletedPreviouslySucceeded,
    confirmationsHydrated,
    initConfirmations,
    purgeStaleConfirmations,
    removeConfirmation,
    submitConfirmationRequested,
    upsertConfirmation,
} from '../actions/confirmation.actions';
import {
    ConfirmationCollectionState,
    ConfirmationIdentityState,
    ConfirmationState,
    initialConfirmationCollectionState,
} from '../confirmation-collection.state';
import { isConfirmationFor } from '../utility/confirmation-utilities';

/**
 * Marks the slice as loading without discarding what is already in it.
 *
 * @param state
 */
export function setConfirmationsLoadingReducer(
    state: Readonly<ConfirmationCollectionState>,
): Readonly<ConfirmationCollectionState> {
    return { ...state, status: LoadStatus.Loading };
}

/**
 * Replaces the collection from the storage read and marks the slice as loaded.
 *
 * @param state the confirmation slice
 * @param props the action payload
 * @param props.confirmations the confirmations read from storage
 * @returns the slice populated from storage and marked as loaded
 */
export function setConfirmationsLoadedReducer(
    state: Readonly<ConfirmationCollectionState>,
    props: { confirmations: ConfirmationState[] },
): Readonly<ConfirmationCollectionState> {
    return { ...state, confirmations: props.confirmations, status: LoadStatus.Loaded };
}

/**
 * Settles the slice from the storage read, with the legacy confirmations merged in.
 *
 * No stored data means no confirmations, not a failure. The persistence layer hands `undefined` to a
 * slice that has nothing stored, to one whose read failed, and to one it does not know — it cannot tell
 * them apart, and deliberately so ("a read/decompress/parse failure is treated as nothing to hydrate").
 * The hydrate effect turns that into an empty list, and settling as loaded on it is what keeps a fresh
 * device usable: `selectConfirmationCollectionLoaded` gates the confirmation route, so a slice left
 * unsettled would hang it.
 *
 * A legacy confirmation only adds one the slice does not already hold: the slice's copy wins. The two can
 * only meet when a device already on the NgRx path downloads a key a legacy device wrote, which a
 * technician working on one device at a time does not do — so the rule is kept as simple as the case is
 * rare, rather than weighing save timestamps across the two stores.
 *
 * @param state the confirmation slice
 * @param props the action payload
 * @param props.confirmations the confirmations read from storage
 * @param props.legacy the legacy confirmations, converted
 * @returns the settled slice
 */
export function hydrateConfirmationsReducer(
    state: Readonly<ConfirmationCollectionState>,
    props: { confirmations: ConfirmationState[]; legacy: ConfirmationState[] },
): Readonly<ConfirmationCollectionState> {
    const migrated = props.legacy.filter(
        (legacy) =>
            !props.confirmations.some((stored) =>
                isConfirmationFor(stored, legacy.notificationId, legacy.confirmationType),
            ),
    );
    return setConfirmationsLoadedReducer(state, { confirmations: [...props.confirmations, ...migrated] });
}

/**
 * Adds the confirmation, or replaces the one already stored for the same notification and type.
 *
 * @param state the confirmation slice
 * @param props the action payload
 * @param props.confirmation the confirmation to store
 * @returns the slice with the confirmation stored
 */
export function upsertConfirmationReducer(
    state: Readonly<ConfirmationCollectionState>,
    props: { confirmation: ConfirmationState },
): Readonly<ConfirmationCollectionState> {
    const incoming = props.confirmation;
    const existingIndex = state.confirmations.findIndex((confirmation) =>
        isConfirmationFor(confirmation, incoming.notificationId, incoming.confirmationType),
    );
    const confirmations =
        existingIndex === -1
            ? [...state.confirmations, incoming]
            : state.confirmations.map((confirmation, index) => (index === existingIndex ? incoming : confirmation));
    return { ...state, confirmations };
}

/**
 * Removes the confirmation for a notification/type pair. A no-op (idempotent) when none is stored.
 *
 * @param state the confirmation slice
 * @param props the action payload
 * @param props.notificationId the notification whose confirmation to remove
 * @param props.confirmationType the confirmation type to remove
 * @returns the slice without that confirmation
 */
export function removeConfirmationReducer(
    state: Readonly<ConfirmationCollectionState>,
    props: ConfirmationIdentityState,
): Readonly<ConfirmationCollectionState> {
    return {
        ...state,
        confirmations: state.confirmations.filter(
            (confirmation) => !isConfirmationFor(confirmation, props.notificationId, props.confirmationType),
        ),
    };
}

/**
 * Keeps only the confirmations that are not among `stale`.
 *
 * @param state the confirmation slice
 * @param props the action payload
 * @param props.stale the confirmations to discard, resolved by the purge effect
 * @returns the slice without the stale confirmations
 */
export function purgeStaleConfirmationsReducer(
    state: Readonly<ConfirmationCollectionState>,
    props: { stale: ConfirmationIdentityState[] },
): Readonly<ConfirmationCollectionState> {
    return {
        ...state,
        confirmations: state.confirmations.filter(
            (confirmation) =>
                !props.stale.some((stale) =>
                    isConfirmationFor(confirmation, stale.notificationId, stale.confirmationType),
                ),
        ),
    };
}

// The submit outcomes (`submitConfirmationSucceeded`/`Aborted`/`Failed`) have no handler here, like
// the query outcomes: the facade awaits them on the action stream by `correlationId`, so there is no
// outcome for the slice to record. The confirmation itself is dropped by `removeConfirmation`, which
// the submit pipeline dispatches once the notification has actually reached CRM.

const createConfirmationReducer = createReducer(
    initialConfirmationCollectionState,
    on(initConfirmations, setConfirmationsLoadingReducer),
    on(confirmationsHydrated, hydrateConfirmationsReducer),
    on(upsertConfirmation, upsertConfirmationReducer),
    // The submit pipeline hands over a full snapshot the technician may never have saved explicitly,
    // so storing it is part of requesting the submit rather than a step the effect has to remember.
    on(submitConfirmationRequested, upsertConfirmationReducer),
    on(removeConfirmation, removeConfirmationReducer),
    // A confirmation completed on another device is dropped once its cleanup has actually succeeded;
    // when the status or storage writes fail it stays, so the technician can retry.
    on(cleanupCompletedPreviouslySucceeded, removeConfirmationReducer),
    on(purgeStaleConfirmations, purgeStaleConfirmationsReducer),
);

export function confirmationCollectionReducer(
    state: Readonly<ConfirmationCollectionState> | undefined,
    action: Action,
): Readonly<ConfirmationCollectionState> {
    return createConfirmationReducer(state, action);
}
